import { CheckCircle2, AlertTriangle, Loader2, Copy, RotateCcw, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { COMPANY } from "@/lib/content";
import { toast } from "sonner";

const Row = ({ state, title, children, testid }) => {
  const icon = state === "pending" ? <Loader2 className="w-5 h-5 animate-spin text-[#154F49]" /> : state === "ok" ? <CheckCircle2 className="w-5 h-5 text-[#154F49]" /> : <AlertTriangle className="w-5 h-5 text-[#9B742A]" />;
  const tone = state === "ok" ? "bg-emerald-50 border-[#154F49]" : state === "pending" ? "bg-[#EDF1E6] border-[#154F49]/20" : "bg-amber-50 border-[#9B742A]";
  return (
    <div className={`flex gap-3 p-4 rounded-2xl border ${tone}`} data-testid={testid} data-state={state}>
      <span className="shrink-0 mt-0.5">{icon}</span>
      <div className="text-sm text-[#1A2E2B] space-y-1"><p className="font-bold text-[#154F49]">{title}</p>{children}</div>
    </div>
  );
};

export const StatusDialog = ({ open, onOpenChange, status, summary, onRetry, onPrint }) => {
  const { portal, email, portalMsg, emailMsg } = status;
  const copy = async () => {
    try { await navigator.clipboard.writeText(summary); toast.success("Formatted application copied."); }
    catch { toast.error("Copy failed — please select the text and copy manually."); }
  };
  const failed = portal === "fail" || email === "fail";
  const done = portal === "ok" && email === "ok";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FBF8F1] border-2 border-[#154F49] rounded-3xl" data-testid="submit-status-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-[#154F49]" data-testid="submit-status-title">{done ? "Application submitted" : "Application status"}</DialogTitle>
          <DialogDescription className="text-[#465F57]">We confirm each step only after the server accepts it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3" role="status" aria-live="polite">
          <Row testid="status-portal" state={portal} title={portal === "pending" ? "Saving your application securely…" : portal === "ok" ? "Application saved with Elevion" : "Your application could not be saved"}>
            {portalMsg && <p>{portalMsg}</p>}
          </Row>
          <Row testid="status-email" state={email} title={email === "pending" ? `Emailing your application to ${COMPANY.email}…` : email === "ok" ? `Delivered to Elevion's hiring inbox (${COMPANY.email})` : email === "skipped" ? "Email not sent" : "Email to the hiring inbox is delayed"}>
            {emailMsg && <p>{emailMsg}</p>}
          </Row>
        </div>
        {failed && (
          <div className="space-y-3 pt-2">
            <label htmlFor="compiledApplicationPreview" className="block text-xs font-bold uppercase tracking-wider text-[#154F49]">Formatted application (backup copy)</label>
            <textarea id="compiledApplicationPreview" readOnly rows={6} value={summary} className="w-full p-3 rounded-2xl border border-[#154F49]/25 bg-white font-mono text-xs text-[#1A2E2B]" data-testid="compiled-application-preview" />
            <div className="grid sm:grid-cols-2 gap-2">
              <button type="button" onClick={onRetry} className="btn-primary justify-center" data-testid="retry-submit-btn"><RotateCcw className="w-4 h-4" /> {portal === "fail" ? "Try submitting again" : "Retry delivery"}</button>
              <button type="button" onClick={copy} className="btn-gold justify-center" data-testid="copy-packet-btn"><Copy className="w-4 h-4" /> Copy my answers</button>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-[#154F49]/15 text-sm text-[#465F57]">
          <button type="button" onClick={onPrint} className="inline-flex items-center gap-2 font-bold text-[#154F49] underline underline-offset-4" data-testid="status-print-btn"><Printer className="w-4 h-4" /> Print / save a PDF copy</button>
          <span>Questions? <a href={COMPANY.phoneHref} className="font-bold text-[#154F49] underline">{COMPANY.phoneDisplay}</a></span>
        </div>
      </DialogContent>
    </Dialog>
  );
};
